<template>
    <EditComponent
        dialog-id="editReputationDialog"
        open-dialog-event="reputation:edit-mode"
        updated-event="reputation:updated"
        :open-dialog-post-function="loadAllReputations"
        :confirm-enabled="false"
        :track-section-opened="EditionOpenedSection.INFLUENCE"
        :track-existing-element="true"
    >
        <template #edit-header>{{ t('profile.reputation.widgets.update.title') }}</template>
        <template #edit-body>
            <span>{{ t('profile.reputation.widgets.update.subtitle') }}</span>
            <div
                v-for="reputation in allReputations"
                :key="reputation.providerName"
                class="reputation-widget__item"
                :class="{'reputation-widget__item-enabled': reputation.enabled}"
                @mouseover="displayAddActionIfNotEnabled(reputation)"
                @mouseleave="displayAddActionIfNotEnabled()"
            >
                <span v-if="!isExternalUserFormEditionModeForCurrent(reputation)">
                    <header class="reputation-widget__item-header">
                        <span class="reputation-widget__item-header-title">
                            {{ t(`profile.reputation.provider.${reputation.providerName}` as TranslationKey) }}
                        </span>
                        <VJoyButton
                            class="reputation-widget__item-header-action"
                            :class="{'reputation-widget__item-header-action-enabled': hover == reputation.providerName || reputation.enabled}"
                            :disabled="externalUserForm.enabled"
                            variant="main"
                            size="small"
                            @click="actionClicked(reputation)"
                        >
                            {{ addOrDeleteLabel(reputation) }}
                        </VJoyButton>
                    </header>
                    <div class="reputation-widget__item-content">
                        <ReputationLogo :reputation="reputation" class="reputation-widget__item-content-logo" />
                        <ReputationStat
                            :reputation="reputation"
                            class="reputation-widget__item-content-stats"
                            :class="{'reputation-widget__item-content-stats-enabled': reputation.enabled}"
                        />
                    </div>
                </span>

                <div
                    class="reputation-widget__item-external-user-form"
                    :class="{
                        'reputation-widget__item-external-user-form-enabled': isExternalUserFormEditionModeForCurrent(reputation),
                    }"
                >
                    <VJoyInput
                        v-model="externalUserForm.userId"
                        name="reputation-user-id"
                        :placeholder="t(`profile.reputation.${reputation.providerName}.userid.prompt.label` as TranslationKey)"
                        class="reputation-widget__item-external-user-form-userid"
                    />

                    <VJoyWrapper justify="flex-end">
                        <VJoyButton size="xsmall" variant="ghost" @click="closeExternalUserForm">{{ t('general.cancel') }}</VJoyButton>
                        <VJoyButton size="xsmall" variant="main" :disabled="!isValidExternalUserForm()" @click="saveExternalUserForm(reputation)">
                            {{ t('general.save') }}
                        </VJoyButton>
                    </VJoyWrapper>
                </div>
            </div>
        </template>
    </EditComponent>
</template>

<script setup lang="ts">
    import {ref, useTranslation} from '#imports';
    import {pushVJoySnackbar, VJoyButton, VJoyInput, VJoyWrapper} from '@maltjoy/core-vue';
    import EditComponent from '~/components/edit/EditComponent.vue';
    import {EditionOpenedSection} from '~/config/TrackingEvents';
    import ReputationLogo from '~/features/reputation-widgets/ReputationLogo.vue';
    import ReputationStat from '~/features/reputation-widgets/ReputationStat.vue';
    import {useReputationStore} from '~/store/reputation/reputationStore';
    import type {ExternalReputationResource} from '~/types/api-raw-types';
    import type {TranslationKey} from '#build/i18n';

    const allReputations = ref<ExternalReputationResource[]>([]);
    const {getReputations, deleteReputation, getAllWidgetsAndReputation, saveReputation} = useReputationStore();

    const {t} = useTranslation();
    const hover = ref();

    type ExternalUserForm = {
        enabled: boolean;
        providerName?: string;
        userId?: string;
    };
    const externalUserForm = ref<ExternalUserForm>({enabled: false});
    const providerAuthWindow = ref();

    function addOrDeleteLabel(reputation: ExternalReputationResource): string {
        return reputation.enabled ? t('generic.form.delete.label') : t('generic.form.add.label');
    }

    function displayAddActionIfNotEnabled(reputation: ExternalReputationResource | null = null): void {
        if (reputation?.enabled) {
            return;
        }

        hover.value = reputation?.providerName;
    }

    function isExternalUserFormEditionModeForCurrent(reputation: ExternalReputationResource): boolean {
        return externalUserForm.value.enabled && externalUserForm.value.providerName === reputation.providerName;
    }

    async function actionClicked(reputation: ExternalReputationResource): Promise<void> {
        if (reputation.enabled) {
            await deleteReputationFor(reputation);
            return;
        }

        if (reputation.authenticationSupportedByProvider) {
            redirectUserToProviderAuthPage(reputation.providerName);
            return;
        }

        externalUserForm.value.providerName = reputation.providerName;
        externalUserForm.value.enabled = true;
    }

    function redirectUserToProviderAuthPage(providerName: string): void {
        providerAuthWindow.value = window.open(`/reputation/${providerName}/login`, 'reputationProviderAuthWindow', 'width=600,height=405');

        const interval = setInterval(() => {
            if (!providerAuthWindow.value || providerAuthWindow.value.closed) {
                clearInterval(interval);
                loadAllReputations();
                getReputations();
            }
        }, 500);
    }

    function isValidExternalUserForm(): boolean {
        return (
            externalUserForm.value.userId !== undefined &&
            externalUserForm.value.userId.trim().length > 0 &&
            externalUserForm.value.providerName !== undefined &&
            externalUserForm.value.providerName.trim().length > 0
        );
    }

    function closeExternalUserForm() {
        externalUserForm.value.enabled = false;
        externalUserForm.value.providerName = undefined;
        externalUserForm.value.userId = undefined;
    }

    async function saveExternalUserForm(reputation: ExternalReputationResource) {
        const userId = externalUserForm.value.userId!;

        try {
            await saveReputation(reputation, userId);
            await loadAllReputations();
            closeExternalUserForm();
        } catch (e) {
            pushVJoySnackbar({
                props: {
                    message: t(`profile.reputation.error.userid.unknown.to.${reputation.providerName}` as TranslationKey, {0: userId}),
                    level: 'error',
                },
            });
        }
    }

    async function deleteReputationFor(reputation: ExternalReputationResource): Promise<void> {
        await deleteReputation(reputation);
        await loadAllReputations();
    }

    async function loadAllReputations() {
        allReputations.value = await getAllWidgetsAndReputation();
    }
</script>

<style lang="scss" scoped>
    .reputation-widget__item {
        position: relative;
        z-index: 0;
        flex: 1 1 350px;
        display: flex;
        flex-direction: column;
        margin: 10px;
        border: 1px solid var(--joy-color-neutral-30);
        border-radius: var(--joy-core-radius-2);
        color: var(--joy-color-secondary-50);

        &-enabled {
            border-color: var(--joy-color-secondary-50);
        }

        &-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px 15px;
            font-size: var(--joy-font-size-primary-600);
            text-transform: capitalize;
            border-bottom: 1px solid var(--joy-color-neutral-10);
            color: var(--joy-color-text-default);

            &-title {
                font-weight: var(--joy-font-weight-bold);
            }

            &-action {
                opacity: 0;

                &-enabled {
                    opacity: 1;
                }
            }
        }

        &-content {
            display: flex;
            padding: 15px;
            background-color: var(--joy-color-neutral-20);

            &-logo {
                display: flex;
                flex-direction: column;
                place-content: stretch space-around;
                align-items: center;
                width: 70px;
            }

            &-stats {
                flex: 1;
                position: relative;
                display: flex;
                flex-wrap: wrap;
                padding-left: 10px;
                margin: -3px -15px;
                list-style-type: none;
                color: var(--joy-color-secondary-50);
                opacity: 0.5;

                &-enabled {
                    opacity: 1;
                }
            }
        }

        &-external-user-form {
            display: none;
            flex-direction: column;
            margin-top: var(--joy-core-spacing-4);

            &-enabled {
                display: flex;
            }

            & > * {
                margin-bottom: var(--joy-core-spacing-4);
                padding: 0 15px;
            }
        }
    }
</style>
